/* ============================================================================
 * Peter and May - Stockist directory
 *
 * The section (sections/pm-stockist.liquid) renders the "nos_detaillants"
 * metaobject through {% paginate ... by 250 %}, because Liquid refuses to
 * iterate more than 250 items in one render and Sage feeds ~690 retailers.
 * This element pulls the remaining pages through the Section Rendering API,
 * then owns sorting, country grouping, text search and the distance sort.
 *
 * Rows carry everything the filtering needs, so there is no second data
 * payload to keep in sync with the markup :
 *   data-search       name + address + postcode + city + country, FR and EN
 *   data-country      display country (downcased by Liquid ; group labels are
 *                     title-cased once by titleCase() below, the no-JS row
 *                     falls back to CSS text-transform)
 *   data-country-alt  the same country in the other language
 *   data-city         display city
 *   data-lat / data-lng
 *
 * Countries arrive in two languages that Sage sometimes swaps, so the country
 * facet is built by buildCountryIndex() : spellings that appear together on a
 * row are merged, the group takes its most frequent spelling as label, and each
 * row's country cell is rewritten to it so the column stays even. Cities get no
 * such treatment on purpose - see the note on data-city in the section.
 * City is a second-level facet, listing only the selected country's cities.
 *
 * Four views, because printing all 689 rows made the page ~52000px tall :
 *   index      no filter at all        the continent grid, the list hidden
 *   continent  ?continent=europe       that continent's countries, same grid
 *   country    ?pays=france            that country's rows, alphabetically by city
 *   results    ?q=... or geolocated    matches across every country
 * The view is derived from the state (see get mode) and the state is mirrored
 * into the URL, so a country (or a continent) is a shareable link and Back
 * walks back out of it one level at a time (see goBack()).
 * Rows are never grouped by city inside a country : Sage lists 117 French towns
 * for 197 French addresses, so city headings would outnumber their own content.
 *
 * Continent has no field on the metaobject, and the country name Sage sends is
 * not reliably in the page's language (the French export holds "RUSSIA", and
 * typos like ALLEMANGNE / ETAT UNIS). COUNTRIES below is the hand-maintained
 * table behind both : it maps the folded FR/EN spellings buildCountryIndex()
 * already merges on to a continent and to a display name per language.
 * Anything not listed falls into "other" and keeps Sage's own spelling rather
 * than disappearing from the grid ; this table was built without a full export
 * of Sage's country list, so it will need revisiting once the actual directory
 * can be checked against it.
 * ========================================================================= */

const EARTH_RADIUS_KM = 6371;
const MI_PER_KM = 0.621371;

/* Geolocated, the city facet has no country to narrow it down, so it lists the
   towns of the nearest retailers instead - this is how many it keeps. Long
   enough to cover a whole metropolitan area, short enough to stay a menu. */
const GEO_CITY_LIMIT = 15;

/* Accent-fold so "Genève" is reachable by typing "geneve" and the country sort
   does not push "Écosse" behind "Zimbabwe" ; also flatten separators and stray
   whitespace, which is what makes Sage's "PAYS-BAS", "PAYS BAS" and "FRANCE "
   collapse onto the country they belong to. */
const fold = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/* URL form of a name : "PAYS-BAS" and "Pays Bas" both land on "pays-bas", which
   is what ?pays= carries. Keyed on the group's display label rather than on its
   union-find root, so the link stays readable and survives a re-index. */
const slugify = (value) => fold(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/* Country labels are downcased at the source (see the Casing note in
   pm-stockist.liquid) - title-cased once here, in buildCountryIndex(), so
   every place that reads countryLabels (the grid, the facet menu and its own
   summary, the breadcrumb, the row and its group heading) gets the same
   already-cased string. Never applied to translated UI copy ("All
   countries") - only to the country names Sage sends. */
const titleCase = (value) =>
  (value || '').replace(/\p{L}+/gu, (word) => word.charAt(0).toUpperCase() + word.slice(1));

/* Country reference table, hand-maintained : Sage has no continent field, and
   it spells countries however the row was typed. Each entry is
   [French name, English name, ...extra folded spellings] and carries :

     - the continent, since the metaobject has none ;
     - the display name in each language, because the label Sage sends is not
       reliably in the page's language - the French export holds "RUSSIA"
       outright, plus typos like ALLEMANGNE, ETAT UNIS and ILES FEREO. Labelling
       a group with its most frequent spelling (which is what buildCountryIndex()
       did on its own) reproduces every one of those on the storefront.

   Lookup keys are folded (see fold() above - lowercase, no accents, hyphens
   flattened to spaces), so both names index themselves and only the spellings
   that fold onto neither need listing as extras. Anything absent from this
   table still works : it lands in the "other" continent and keeps Sage's own
   spelling, title-cased. Not exhaustive - see the note at the top of this
   file. */
const COUNTRIES = {
  europe: [
    ['France', 'France'],
    ['Allemagne', 'Germany', 'allemangne'],
    ['Italie', 'Italy'],
    ['Espagne', 'Spain'],
    ['Portugal', 'Portugal'],
    ['Belgique', 'Belgium'],
    ['Pays-Bas', 'Netherlands', 'hollande', 'holland'],
    ['Suisse', 'Switzerland'],
    ['Autriche', 'Austria'],
    ['Grèce', 'Greece'],
    ['Pologne', 'Poland'],
    ['République tchèque', 'Czech Republic', 'tchequie', 'czechia'],
    ['Danemark', 'Denmark'],
    ['Suède', 'Sweden'],
    ['Norvège', 'Norway'],
    ['Finlande', 'Finland'],
    ['Irlande', 'Ireland'],
    ['Luxembourg', 'Luxembourg'],
    ['Chypre', 'Cyprus'],
    ['Malte', 'Malta'],
    ['Islande', 'Iceland'],
    ['Îles Féroé', 'Faroe Islands', 'iles fereo'],
    ['Hongrie', 'Hungary'],
    ['Roumanie', 'Romania'],
    ['Bulgarie', 'Bulgaria'],
    ['Croatie', 'Croatia'],
    ['Slovénie', 'Slovenia'],
    ['Slovaquie', 'Slovakia'],
    ['Estonie', 'Estonia'],
    ['Lettonie', 'Latvia'],
    ['Lituanie', 'Lithuania'],
    ['Monaco', 'Monaco'],
    ['Andorre', 'Andorra'],
    ['Liechtenstein', 'Liechtenstein'],
    ['Royaume-Uni', 'United Kingdom', 'uk'],
    ['Écosse', 'Scotland'],
    ['Pays de Galles', 'Wales'],
    ['Serbie', 'Serbia'],
    ['Bosnie-Herzégovine', 'Bosnia', 'bosnie'],
    ['Monténégro', 'Montenegro'],
    ['Macédoine', 'Macedonia'],
    ['Albanie', 'Albania'],
    ['Ukraine', 'Ukraine'],
    ['Moldavie', 'Moldova'],
    ['Russie', 'Russia'],
    ['Biélorussie', 'Belarus'],
  ],
  'north-america': [
    ['États-Unis', 'United States', 'usa', 'etat unis'],
    ['Canada', 'Canada'],
    ['Mexique', 'Mexico'],
  ],
  'south-america': [
    ['Brésil', 'Brazil'],
    ['Argentine', 'Argentina'],
    ['Chili', 'Chile'],
    ['Colombie', 'Colombia'],
    ['Pérou', 'Peru'],
    ['Uruguay', 'Uruguay'],
    ['Équateur', 'Ecuador'],
    ['Venezuela', 'Venezuela'],
    ['Bolivie', 'Bolivia'],
    ['Paraguay', 'Paraguay'],
  ],
  asia: [
    ['Japon', 'Japan'],
    ['Chine', 'China'],
    ['Corée du Sud', 'South Korea', 'korea', 'coree'],
    ['Hong Kong', 'Hong Kong'],
    ['Singapour', 'Singapore'],
    ['Taïwan', 'Taiwan'],
    ['Thaïlande', 'Thailand'],
    ['Inde', 'India'],
    ['Vietnam', 'Vietnam'],
    ['Malaisie', 'Malaysia'],
    ['Indonésie', 'Indonesia'],
    ['Philippines', 'Philippines'],
    ['Cambodge', 'Cambodia'],
    ['Sri Lanka', 'Sri Lanka'],
    ['Pakistan', 'Pakistan'],
    ['Bangladesh', 'Bangladesh'],
    ['Kazakhstan', 'Kazakhstan'],
  ],
  'middle-east': [
    ['Émirats arabes unis', 'United Arab Emirates', 'uae'],
    ['Qatar', 'Qatar'],
    ['Arabie saoudite', 'Saudi Arabia'],
    ['Israël', 'Israel'],
    ['Koweït', 'Kuwait'],
    ['Liban', 'Lebanon'],
    ['Turquie', 'Turkey'],
    ['Bahreïn', 'Bahrain'],
    ['Oman', 'Oman'],
    ['Jordanie', 'Jordan'],
    ['Iran', 'Iran'],
    ['Irak', 'Iraq'],
  ],
  africa: [
    ['Maroc', 'Morocco'],
    ['Tunisie', 'Tunisia'],
    ['Afrique du Sud', 'South Africa'],
    ['Égypte', 'Egypt'],
    ['Algérie', 'Algeria'],
    ['Sénégal', 'Senegal'],
    ["Côte d'Ivoire", 'Ivory Coast'],
    ['Kenya', 'Kenya'],
    ['Nigeria', 'Nigeria'],
    ['Ghana', 'Ghana'],
    ['Cameroun', 'Cameroon'],
    ['Île Maurice', 'Mauritius'],
    ['Réunion', 'Reunion'],
  ],
  oceania: [
    ['Australie', 'Australia'],
    ['Nouvelle-Zélande', 'New Zealand'],
    ['Polynésie française', 'French Polynesia'],
    ['Nouvelle-Calédonie', 'New Caledonia'],
    ['Fidji', 'Fiji'],
  ],
};

const COUNTRY_CONTINENT = new Map();
const COUNTRY_NAMES = new Map();
Object.keys(COUNTRIES).forEach((continent) => {
  COUNTRIES[continent].forEach(([fr, en, ...extras]) => {
    const record = { fr, en };
    new Set([fold(fr), fold(en), ...extras]).forEach((key) => {
      COUNTRY_CONTINENT.set(key, continent);
      COUNTRY_NAMES.set(key, record);
    });
  });
});

/* Tries every spelling a row carries (FR then EN, see item.countryNames) and
   falls back to "other" - a country Sage sends that isn't in the table above
   still gets its own place in the grid instead of quietly vanishing from it. */
const continentOf = (names) => {
  for (const name of names) {
    if (COUNTRY_CONTINENT.has(name)) return COUNTRY_CONTINENT.get(name);
  }
  return 'other';
};

const toRad = (deg) => (deg * Math.PI) / 180;

function haversineKm(from, to) {
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

if (!customElements.get('pm-stockist')) {
  customElements.define(
    'pm-stockist',
    class PmStockist extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-stockist-list]');
        if (!this.list) return;

        this.input = this.querySelector('[data-stockist-search]');
        this.submitBtn = this.querySelector('[data-stockist-search-submit]');
        this.searchClearBtn = this.querySelector('[data-stockist-search-clear]');
        this.countryEl = this.querySelector('[data-stockist-country]');
        this.countryList = this.querySelector('[data-stockist-country-list]');
        this.countryLabelEl = this.querySelector('[data-stockist-country-label]');
        this.cityEl = this.querySelector('[data-stockist-city]');
        this.cityList = this.querySelector('[data-stockist-city-list]');
        this.cityLabelEl = this.querySelector('[data-stockist-city-label]');
        this.clearBtn = this.querySelector('[data-stockist-clear]');
        this.nearBtn = this.querySelector('[data-stockist-near]');
        this.countEl = this.querySelector('[data-stockist-count-wrap]');
        this.messageEl = this.querySelector('[data-stockist-message]');
        this.emptyEl = this.querySelector('[data-stockist-empty]');
        this.paginationEl = this.querySelector('[data-stockist-pagination]');
        this.countriesEl = this.querySelector('[data-stockist-countries]');
        this.crumbEl = this.querySelector('[data-stockist-crumb]');
        this.crumbTitleEl = this.querySelector('[data-stockist-crumb-title]');
        this.backBtn = this.querySelector('[data-stockist-back]');
        this.backLabelEl = this.querySelector('[data-stockist-back-label]');
        this.citiesEl = this.querySelector('[data-stockist-cities]');
        this.citiesListEl = this.querySelector('[data-stockist-cities-list]');
        this.loadingEl = this.querySelector('[data-stockist-loading]');

        this.pages = parseInt(this.dataset.pages, 10) || 1;
        this.pageParam = this.dataset.pageParam || 'page';
        this.total = parseInt(this.dataset.total, 10) || 0;
        this.unit = this.dataset.unit === 'mi' ? 'mi' : 'km';
        // Which column of COUNTRIES the labels come from. Only French has one
        // of its own - every other storefront language reads the English
        // names, the same way the section falls back to en_pays.
        this.lang = (this.dataset.lang || 'en').toLowerCase();
        this.countLabel = this.dataset.countLabel || '[count]';
        this.allCountriesLabel = this.dataset.allCountries || 'All countries';
        this.allCitiesLabel = this.dataset.allCities || 'All cities';
        // Folded, so the setting matches the group label whatever its case or
        // accents ("France" against Sage's "FRANCE").
        this.pinnedCountry = fold(this.dataset.pinnedCountry);
        this.continentLabels = {
          europe: this.dataset.continentEurope || 'Europe',
          'north-america': this.dataset.continentNorthAmerica || 'North America',
          'south-america': this.dataset.continentSouthAmerica || 'South America',
          asia: this.dataset.continentAsia || 'Asia',
          'middle-east': this.dataset.continentMiddleEast || 'Middle East',
          africa: this.dataset.continentAfrica || 'Africa',
          oceania: this.dataset.continentOceania || 'Oceania',
          other: this.dataset.continentOther || 'Other',
        };
        // Captured from the SSR text before anything rewrites it - the only
        // copy of "All continents" the element carries (no data attribute for
        // it, same as t_back never having a merchant setting).
        this.allContinentsLabel = this.backLabelEl ? this.backLabelEl.textContent : 'All continents';

        this.items = [];
        this.groups = new Map();
        this.countryLabels = new Map();
        this.countryCounts = new Map();
        this.countryContinents = new Map();
        this.continentCounts = new Map();
        this.countryBySlug = new Map();
        this.slugByCountry = new Map();
        this.query = '';
        this.country = '';
        this.continent = '';
        this.city = '';
        this.origin = null;
        this.hydrated = this.pages < 2;
        this.degraded = false;
        this.renderedMode = null;

        this.bind();
        this.collect();

        if (this.hydrated) {
          this.applyUrlState();
          this.render();
        } else {
          // The country grid needs every row. Built on page 1 alone it would
          // list the wrong countries with the wrong counts and then visibly
          // rewrite itself, so nothing is shown until the rest lands.
          this.setLoading(true);
          this.hydrate();
        }
      }

      /* Which of the four views the current state adds up to. Geolocation wins
         over everything : a distance-sorted list only means something flat.
         A country always wins over a continent - goToCountry() keeps them in
         sync (see there), so this never has to choose between the two. */
      get mode() {
        if (this.degraded || this.origin) return 'results';
        if (this.country) return 'country';
        if (this.continent) return 'continent';
        if (this.query) return 'results';
        return 'index';
      }

      bind() {
        if (this.input) {
          this.input.addEventListener('input', () => {
            // The cross tracks the field itself, not the debounced query -
            // it has to appear on the first keystroke, not 120ms later.
            this.syncSearchClear();
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.applyQuery(), 120);
          });
          this.input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.applyQuery();
            }
          });
        }
        if (this.submitBtn) this.submitBtn.addEventListener('click', () => this.applyQuery());
        // Empties the field and re-runs the search : emptying it silently would
        // leave the results of a query that is no longer written anywhere.
        if (this.searchClearBtn) {
          this.searchClearBtn.addEventListener('click', () => {
            if (!this.input) return;
            this.input.value = '';
            this.syncSearchClear();
            clearTimeout(this.searchTimer);
            this.applyQuery();
            this.input.focus();
          });
        }
        if (this.nearBtn) this.nearBtn.addEventListener('click', () => this.toggleGeo());
        if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearAll());
        if (this.backBtn) this.backBtn.addEventListener('click', () => this.goBack());

        // Entering a country is a pushState, so Back has to walk out of it.
        this.popHandler = () => this.applyUrlState(true);
        window.addEventListener('popstate', this.popHandler);

        // The facets are bare <details> - close them on outside click / Escape.
        this.facets = [this.countryEl, this.cityEl].filter(Boolean);
        if (this.facets.length) {
          this.outsideHandler = (event) => {
            this.facets.forEach((facet) => {
              if (facet.open && !facet.contains(event.target)) facet.open = false;
            });
          };
          this.keyHandler = (event) => {
            if (event.key !== 'Escape') return;
            this.facets.forEach((facet) => {
              facet.open = false;
            });
          };
          document.addEventListener('click', this.outsideHandler);
          document.addEventListener('keydown', this.keyHandler);
        }
      }

      disconnectedCallback() {
        clearTimeout(this.searchTimer);
        if (this.outsideHandler) document.removeEventListener('click', this.outsideHandler);
        if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler);
        if (this.popHandler) window.removeEventListener('popstate', this.popHandler);
      }

      /* --- data ------------------------------------------------------------ */

      collect() {
        this.items = Array.from(this.list.querySelectorAll('[data-stockist-item]')).map((el) => {
          const country = el.dataset.country || '';
          const city = el.dataset.city || '';
          const nameEl = el.querySelector('.pm-stockist__name');
          const lat = parseFloat(el.dataset.lat);
          const lng = parseFloat(el.dataset.lng);
          return {
            el,
            country,
            countryNames: Array.from(new Set([fold(country), fold(el.dataset.countryAlt)].filter(Boolean))),
            groupKey: fold(country),
            groupLabel: country,
            groupSort: fold(country),
            countryEl: el.querySelector('.pm-stockist__country'),
            city,
            cityKey: fold(city),
            citySort: fold(city),
            nameKey: fold(nameEl ? nameEl.textContent : ''),
            search: fold(el.dataset.search),
            lat: Number.isFinite(lat) ? lat : null,
            lng: Number.isFinite(lng) ? lng : null,
            distanceEl: el.querySelector('[data-stockist-distance]'),
            distance: null,
          };
        });
        this.indexCountries();
      }

      /* Merges the country spellings, labels every group and applies the label
         to each row. Run once per dataset change rather than once per repaint :
         the union-find below is the only O(n log n) work on this page. */
      indexCountries() {
        this.countryLabels = this.buildCountryIndex();
        this.countryCounts = new Map();
        this.countryContinents = new Map();
        this.continentCounts = new Map();
        this.countryBySlug = new Map();
        this.slugByCountry = new Map();

        this.items.forEach((item) => {
          item.groupLabel = this.countryLabels.get(item.groupKey) || item.country;
          item.groupSort = fold(item.groupLabel);
          this.countryCounts.set(item.groupKey, (this.countryCounts.get(item.groupKey) || 0) + 1);
          // One continent per group, resolved once from whichever row got
          // there first - every row in the group carries the same spellings.
          if (!this.countryContinents.has(item.groupKey)) {
            this.countryContinents.set(item.groupKey, continentOf(item.countryNames));
          }
          const continent = this.countryContinents.get(item.groupKey);
          this.continentCounts.set(continent, (this.continentCounts.get(continent) || 0) + 1);
          // The country column is rewritten to the group's label so the column
          // reads evenly whichever spelling Sage sent on that row.
          if (item.countryEl && item.countryEl.textContent !== item.groupLabel) {
            item.countryEl.textContent = item.groupLabel;
          }
        });

        this.countryLabels.forEach((label, key) => {
          let slug = slugify(label) || key;
          // Two groups can only collide here through a Sage oddity ; suffixing
          // keeps both reachable rather than letting one shadow the other.
          if (this.countryBySlug.has(slug)) slug = `${slug}-${this.countryBySlug.size}`;
          this.countryBySlug.set(slug, key);
          this.slugByCountry.set(key, slug);
        });
      }

      /* Pages 2..n of the paginated section, fetched for their rows only. */
      async hydrate() {
        if (this.pages < 2) return;

        const requests = [];
        for (let page = 2; page <= this.pages; page += 1) {
          // Built off the live URL so an alternate ?view= (used to preview this
          // template before the page's suffix is repointed) survives the fetch.
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set(this.pageParam, page);
          requests.push(
            fetch(url.toString())
              .then((response) => (response.ok ? response.text() : ''))
              .catch(() => '')
          );
        }

        const chunks = await Promise.all(requests);
        const parser = new DOMParser();
        const frag = document.createDocumentFragment();

        chunks.forEach((html) => {
          if (!html) return;
          const doc = parser.parseFromString(html, 'text/html');
          doc.querySelectorAll('[data-stockist-list] > [data-stockist-item]').forEach((row) => {
            frag.appendChild(row);
          });
        });

        this.hydrated = true;
        this.setLoading(false);

        if (!frag.childElementCount) {
          // Every extra page failed. A country grid built on the 250 rows that
          // did arrive would be a lie, so the element drops back to the plain
          // flat list and keeps the server pagination as the way through.
          this.degraded = true;
          this.applyUrlState();
          this.render();
          return;
        }

        this.list.appendChild(frag);
        if (this.paginationEl) {
          this.paginationEl.remove();
          this.paginationEl = null;
        }
        this.collect();
        if (this.origin) this.applyDistances();
        this.applyUrlState();
        this.render();
      }

      /* --- rendering -------------------------------------------------------- */

      /* Light repaint - filters and re-chromes without touching the DOM order.
         The heavy render() below is only needed when the sort or the headings
         change : re-ordering 689 <li> on every keystroke is what would make
         this page crawl. */
      refresh() {
        if (this.mode !== this.renderedMode) {
          this.render();
          return;
        }
        this.buildCountryMenu();
        this.buildCityMenu();
        this.buildCityChips();
        this.filter();
        this.syncChrome();
      }

      /* Sorts the rows and rebuilds the country headings. Detached from the
         document first : moving ~690 <li> one by one in a live list is the one
         thing that makes this page feel slow. */
      render() {
        const geo = Boolean(this.origin);
        // Country headings belong to the cross-country views only. Inside a
        // country they would be one heading repeated ; by city they would
        // outnumber the rows (117 French towns for 197 French addresses).
        const headings = !geo && this.mode === 'results';

        const sorted = this.items.slice();

        if (geo) {
          sorted.sort((a, b) => {
            const da = a.distance === null ? Infinity : a.distance;
            const db = b.distance === null ? Infinity : b.distance;
            return da - db;
          });
        } else {
          sorted.sort(
            (a, b) =>
              a.groupSort.localeCompare(b.groupSort) ||
              a.citySort.localeCompare(b.citySort) ||
              a.nameKey.localeCompare(b.nameKey)
          );
        }

        const parent = this.list.parentNode;
        const anchor = this.list.nextSibling;
        parent.removeChild(this.list);

        this.groups.forEach((heading) => heading.remove());
        this.groups.clear();

        const frag = document.createDocumentFragment();
        let currentCountry = null;

        sorted.forEach((item) => {
          if (headings && item.groupKey && item.groupKey !== currentCountry) {
            currentCountry = item.groupKey;
            const heading = document.createElement('li');
            heading.className = 'pm-stockist__group t-chapter-title';
            heading.textContent = item.groupLabel;
            this.groups.set(item.groupKey, heading);
            frag.appendChild(heading);
          }
          frag.appendChild(item.el);
        });

        this.list.appendChild(frag);
        parent.insertBefore(this.list, anchor);

        this.buildCountryMenu();
        this.buildCityMenu();
        this.buildCityChips();
        this.buildIndexGrid();
        this.filter();
        this.syncChrome();
        this.renderedMode = this.mode;
      }

      /* --- views ------------------------------------------------------------ */

      /* Shows the one view the current state adds up to. Runs after filter(),
         which is what lets it override the empty state a list that is not even
         on screen would otherwise raise. */
      syncChrome() {
        const mode = this.mode;
        this.dataset.mode = mode;
        const grid = mode === 'index' || mode === 'continent';

        if (this.countriesEl) this.countriesEl.hidden = !grid;
        if (this.list) this.list.hidden = grid;
        if (this.emptyEl && grid) this.emptyEl.hidden = true;
        if (this.crumbEl) this.crumbEl.hidden = mode !== 'country' && mode !== 'continent';
        if (this.crumbTitleEl) {
          if (mode === 'country' && this.country) {
            this.crumbTitleEl.textContent = this.countryLabels.get(this.country) || '';
          } else if (mode === 'continent' && this.continent) {
            this.crumbTitleEl.textContent = this.continentLabel(this.continent);
          }
        }
        // "All continents" at the very top, the country's own continent one
        // level down - never a fixed label, unlike the rest of the crumb.
        if (this.backLabelEl) {
          this.backLabelEl.textContent =
            mode === 'country' ? this.continentLabel(this.continent) : this.allContinentsLabel;
        }
      }

      continentLabel(key) {
        return this.continentLabels[key] || '';
      }

      /* Busiest continent first - a handful of continents needs no pinning
         setting the way the ~40 countries do, the retailer count already
         puts the home market's continent on top. Alphabetical only breaks a
         tie between two continents holding the same count. */
      sortedContinents() {
        return Array.from(this.continentCounts.keys()).sort((a, b) => {
          const byCount = (this.continentCounts.get(b) || 0) - (this.continentCounts.get(a) || 0);
          if (byCount !== 0) return byCount;
          return fold(this.continentLabel(a)).localeCompare(fold(this.continentLabel(b)));
        });
      }

      /* Reuses sortedCountries() (pinned country first, then alphabetical) and
         filters it down - the continent view is a subset of the same list,
         never a separately sorted one. */
      countriesInContinent(key) {
        return this.sortedCountries().filter(([groupKey]) => this.countryContinents.get(groupKey) === key);
      }

      /* The continent/country grid : the top of the hierarchy, ~7 continents
         or a handful of countries instead of 689 rows. Rebuilt on every level
         change (see render()) rather than built once - unlike the 690-row
         sort this is cheap, and unlike that list its content actually changes
         as the visitor moves between levels. */
      buildIndexGrid() {
        if (!this.countriesEl) return;
        const mode = this.mode;
        if (mode !== 'index' && mode !== 'continent') return;

        const rows =
          mode === 'index'
            ? this.sortedContinents().map((key) => [key, this.continentLabel(key), this.continentCounts.get(key) || 0])
            : this.countriesInContinent(this.continent).map(([key, label]) => [key, label, this.countryCounts.get(key) || 0]);
        const onSelect = mode === 'index' ? (key) => this.goToContinent(key) : (key) => this.goToCountry(key);

        const frag = document.createDocumentFragment();
        rows.forEach(([key, label, count]) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pm-stockist__country-link';

          const name = document.createElement('span');
          name.className = 'pm-stockist__country-name t-sub-header';
          name.textContent = label;

          const total = document.createElement('span');
          total.className = 'pm-stockist__country-total t-card-ui-2';
          total.textContent = count;

          button.append(name, total);
          button.addEventListener('click', () => onSelect(key));
          frag.appendChild(button);
        });

        this.countriesEl.replaceChildren(frag);
      }

      /* City index, country view only. Sage's directory is mostly one address
         per town, so listing every city would just be the wall again : only the
         cities holding several addresses earn a chip, biggest first. */
      buildCityChips() {
        if (!this.citiesEl || !this.citiesListEl) return;

        if (this.mode !== 'country') {
          this.citiesEl.hidden = true;
          return;
        }

        const counts = new Map();
        const labels = new Map();
        this.items.forEach((item) => {
          if (item.groupKey !== this.country || !item.cityKey) return;
          counts.set(item.cityKey, (counts.get(item.cityKey) || 0) + 1);
          if (!labels.has(item.cityKey)) labels.set(item.cityKey, item.city);
        });

        const dense = Array.from(counts.entries())
          .filter(([, count]) => count > 1)
          .sort((a, b) => b[1] - a[1] || fold(labels.get(a[0])).localeCompare(fold(labels.get(b[0]))))
          .slice(0, 12);

        // Below three there is nothing to index - the list itself is the index.
        if (dense.length < 3) {
          this.citiesEl.hidden = true;
          return;
        }

        const frag = document.createDocumentFragment();
        dense.forEach(([key, count]) => {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pm-stockist__city-chip t-body-inter';
          if (key === this.city) button.setAttribute('aria-current', 'true');

          const name = document.createElement('span');
          name.className = 'pm-stockist__city-chip-name';
          name.textContent = labels.get(key);
          const badge = document.createElement('span');
          badge.className = 'pm-stockist__city-chip-count';
          badge.textContent = count;

          button.append(name, badge);
          // A second click on the live chip lets the whole country back in.
          button.addEventListener('click', () => this.selectCity(key === this.city ? '' : key));
          li.appendChild(button);
          frag.appendChild(li);
        });

        this.citiesListEl.replaceChildren(frag);
        this.citiesEl.hidden = false;
      }

      /* --- navigation -------------------------------------------------------- */

      goToCountry(key) {
        this.country = key;
        // Always resolvable - continentOf() never returns empty, "other" is
        // itself a bucket - so the breadcrumb can always name a continent to
        // go back to, even for a country reached straight from the facet
        // dropdown or a shared ?pays= link that never set ?continent=.
        this.continent = this.countryContinents.get(key) || '';
        this.city = '';
        if (this.countryLabelEl) {
          this.countryLabelEl.textContent = this.countryLabels.get(key) || this.allCountriesLabel;
        }
        if (this.cityLabelEl) this.cityLabelEl.textContent = this.allCitiesLabel;
        this.syncUrl();
        this.refresh();
        this.scrollToTop();
      }

      goToContinent(key) {
        this.continent = key;
        this.country = '';
        this.city = '';
        if (this.countryLabelEl) this.countryLabelEl.textContent = this.allCountriesLabel;
        if (this.cityLabelEl) this.cityLabelEl.textContent = this.allCitiesLabel;
        this.syncUrl();
        this.refresh();
        this.scrollToTop();
      }

      goToIndex() {
        this.continent = '';
        this.country = '';
        this.city = '';
        if (this.countryLabelEl) this.countryLabelEl.textContent = this.allCountriesLabel;
        if (this.cityLabelEl) this.cityLabelEl.textContent = this.allCitiesLabel;
        this.syncUrl();
        this.refresh();
        this.scrollToTop();
      }

      /* The breadcrumb's back button : one level up, not straight to the top -
         a country goes back to its own continent, a continent goes back to
         the index. */
      goBack() {
        if (this.mode === 'country') this.goToContinent(this.continent);
        else this.goToIndex();
      }

      selectCity(key) {
        this.city = key;
        if (this.cityLabelEl) {
          this.cityLabelEl.textContent = key ? this.cityLabel(key) : this.allCitiesLabel;
        }
        this.syncUrl();
        this.refresh();
      }

      /* Changing view while parked halfway down the previous one would drop the
         visitor into the middle of the new one. Only pulls up, never down. */
      scrollToTop() {
        const top = this.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY <= top) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
      }

      /* --- URL state --------------------------------------------------------- */

      cityLabel(key) {
        const match = this.items.find((item) => item.cityKey === key);
        return match ? match.city : '';
      }

      cityKeyFromSlug(country, slug) {
        if (!slug) return '';
        const match = this.items.find(
          (item) => item.groupKey === country && item.cityKey && slugify(item.city) === slug
        );
        return match ? match.cityKey : '';
      }

      /* Reads ?pays / ?continent / ?ville / ?q back into the element. Called
         once the data is complete (the slugs resolve against the country
         index) and again on every popstate, which is what makes Back walk out
         of a country or a continent. A country always wins over ?continent= -
         its own continent is resolved from the lookup instead, so a stale or
         missing continent param next to a valid ?pays= never breaks anything. */
      applyUrlState(fromPop) {
        const params = new URLSearchParams(window.location.search);
        const country = this.countryBySlug.get(params.get('pays') || '') || '';
        const continentParam = params.get('continent') || '';

        this.country = this.degraded ? '' : country;
        this.continent = this.country
          ? this.countryContinents.get(this.country) || ''
          : this.continentLabel(continentParam)
            ? continentParam
            : '';
        this.city = this.country ? this.cityKeyFromSlug(this.country, params.get('ville') || '') : '';
        this.query = params.get('q') || '';

        if (this.input) this.input.value = this.query;
        this.syncSearchClear();
        if (this.countryLabelEl) {
          this.countryLabelEl.textContent = this.country
            ? this.countryLabels.get(this.country) || this.allCountriesLabel
            : this.allCountriesLabel;
        }
        if (this.cityLabelEl) {
          this.cityLabelEl.textContent = this.city ? this.cityLabel(this.city) : this.allCitiesLabel;
        }

        if (fromPop) this.refresh();
      }

      /* Mirrors the state into the URL, leaving every other parameter alone -
         ?view= in particular, which is how this template is previewed. */
      syncUrl(replace) {
        const url = new URL(window.location.href);
        const set = (key, value) => {
          if (value) url.searchParams.set(key, value);
          else url.searchParams.delete(key);
        };
        set('pays', this.country ? this.slugByCountry.get(this.country) || '' : '');
        // Implicit once a country is set - the country resolves its own
        // continent, so carrying both in the URL would just be clutter.
        set('continent', !this.country && this.continent ? this.continent : '');
        // Only inside a country : a geolocated city is scoped to a position
        // the URL does not carry, so ?ville= alone would come back on reload
        // pointing at a filter the page can no longer apply (applyUrlState
        // drops it), and the address bar would disagree with the list.
        set('ville', this.city && this.country ? slugify(this.cityLabel(this.city)) : '');
        set('q', this.query);

        const next = url.toString();
        if (next === window.location.href) return;
        // Entering or leaving a country is a navigation and Back must undo it ;
        // typing in the search box is not, or Back would replay every keystroke.
        if (replace) window.history.replaceState({}, '', next);
        else window.history.pushState({}, '', next);
      }

      /* The cross is chrome on the field, so it follows the field's own value
         rather than this.query - which lags a keystroke behind it. */
      syncSearchClear() {
        if (!this.searchClearBtn) return;
        this.searchClearBtn.hidden = !(this.input && this.input.value);
      }

      setLoading(on) {
        if (this.loadingEl) this.loadingEl.hidden = !on;
        if (this.list) this.list.hidden = on;
        if (this.countriesEl && on) this.countriesEl.hidden = true;
        if (this.countEl) this.countEl.hidden = on;
      }

      /* One entry per country, however many spellings Sage uses for it.
         pays + en_pays on the same row is a link between two spellings ; a
         union-find over those links merges ITALIE/ITALY, PAYS-BAS/PAYS BAS/
         NETHERLANDS, ALLEMANGNE/ALLEMAGNE, and keeps rows whose en_pays is
         empty in the right group. Each group is labelled with its most frequent
         spelling.

         The dominance test is what keeps that safe. Sage has one row pairing
         FRANCE with USA ; taken at face value it files every American retailer
         under France. A link is only trusted when it carries at least half the
         rows of the rarer of the two spellings - true of every real FR/EN pair
         (ITALIE/ITALY is 110 of 110), false of a one-off mistake against a
         197-row country. Typos still merge, because the typo itself is rare. */
      /* The group's display name in the page's language, whatever Sage wrote
         on the rows - the French export labels its Russian retailers "RUSSIA".
         Empty when no spelling in the group is in COUNTRIES, which sends the
         caller back to Sage's own most frequent spelling. */
      canonicalCountryName(names) {
        for (const name of names) {
          const record = COUNTRY_NAMES.get(name);
          if (record) return this.lang === 'fr' ? record.fr : record.en;
        }
        return '';
      }

      buildCountryIndex() {
        const totals = new Map();
        const links = new Map();

        this.items.forEach((item) => {
          const names = item.countryNames;
          names.forEach((name) => totals.set(name, (totals.get(name) || 0) + 1));
          if (names.length === 2) {
            // Folded country names contain spaces ("pays bas"), so each link
            // keeps its own endpoints rather than a joined key to re-split.
            const pair = names.slice().sort();
            const key = pair.join('\n');
            const link = links.get(key);
            if (link) link.weight += 1;
            else links.set(key, { a: pair[0], b: pair[1], weight: 1 });
          }
        });

        const parent = new Map();
        totals.forEach((count, name) => parent.set(name, name));
        const find = (name) => {
          let root = name;
          while (parent.get(root) !== root) root = parent.get(root);
          let cursor = name;
          while (parent.get(cursor) !== root) {
            const next = parent.get(cursor);
            parent.set(cursor, root);
            cursor = next;
          }
          return root;
        };

        links.forEach((link) => {
          if (link.weight * 2 < Math.min(totals.get(link.a), totals.get(link.b))) return;
          const rootA = find(link.a);
          const rootB = find(link.b);
          if (rootA !== rootB) parent.set(rootA, rootB);
        });

        const tally = new Map();
        // Every spelling the merged group holds, so the canonical lookup can be
        // tried against all of them rather than only the one Sage used most.
        const groupNames = new Map();
        this.items.forEach((item) => {
          const label = item.country.trim();
          if (!item.countryNames.length || !label) return;
          item.groupKey = find(item.countryNames[0]);
          if (!tally.has(item.groupKey)) tally.set(item.groupKey, new Map());
          const counts = tally.get(item.groupKey);
          counts.set(label, (counts.get(label) || 0) + 1);
          if (!groupNames.has(item.groupKey)) groupNames.set(item.groupKey, new Set());
          const names = groupNames.get(item.groupKey);
          item.countryNames.forEach((name) => names.add(name));
        });

        const labels = new Map();
        tally.forEach((counts, key) => {
          let best = '';
          let bestCount = -1;
          counts.forEach((count, label) => {
            if (count > bestCount) {
              best = label;
              bestCount = count;
            }
          });
          // A known country is named from the table, in the page's language ;
          // anything else keeps Sage's most frequent spelling, title-cased.
          const canonical = this.canonicalCountryName(groupNames.get(key) || []);
          labels.set(key, canonical || titleCase(best));
        });
        return labels;
      }

      /* Alphabetical, except for the home market, which is pinned to the top of
         both the index and the filter : it holds a third of the directory and
         sending most visitors hunting through the Fs is pure friction. An empty
         or unmatched setting simply leaves the list alphabetical. */
      sortedCountries() {
        const pinned = this.pinnedCountry;
        return Array.from(this.countryLabels.entries()).sort((a, b) => {
          if (pinned) {
            const rankA = fold(a[1]) === pinned ? 0 : 1;
            const rankB = fold(b[1]) === pinned ? 0 : 1;
            if (rankA !== rankB) return rankA - rankB;
          }
          return fold(a[1]).localeCompare(fold(b[1]));
        });
      }

      buildCountryMenu() {
        if (!this.countryList) return;

        const frag = document.createDocumentFragment();
        frag.appendChild(this.facetOption('country', '', this.allCountriesLabel));
        this.sortedCountries().forEach(([key, label]) =>
          frag.appendChild(this.facetOption('country', key, label))
        );

        this.countryList.replaceChildren(frag);
      }

      /* Second level : only the cities of the selected country, so the menu
         stays a few dozen entries instead of the ~450 the full directory holds.
         Geolocation is the other way in - it has no country, but it does have
         an order, so the facet lists the GEO_CITY_LIMIT nearest towns, nearest
         first. Left to the country alone it sat greyed out for the whole
         "Localiser un revendeur" flow, which is the one flow where picking a
         town two stops down the line is the obvious next move.
         With neither a country nor a position there is nothing sensible to
         list, and the facet is marked disabled rather than shown empty. */
      buildCityMenu() {
        if (!this.cityList) return;

        const geo = !this.country && Boolean(this.origin);
        const enabled = Boolean(this.country) || geo;
        if (this.cityEl) {
          this.cityEl.setAttribute('aria-disabled', String(!enabled));
          if (!enabled) this.cityEl.open = false;
        }

        const cities = new Map();
        if (geo) {
          // Distance order, so the menu reads as "the towns around you" and
          // not as a truncated alphabet. Rows with no coordinates carry no
          // distance and would otherwise sort to the front of the list.
          this.items
            .filter((item) => item.cityKey && item.distance !== null)
            .sort((a, b) => a.distance - b.distance)
            .some((item) => {
              if (!cities.has(item.cityKey)) cities.set(item.cityKey, item.city);
              return cities.size >= GEO_CITY_LIMIT;
            });
        } else if (enabled) {
          this.items.forEach((item) => {
            if (item.groupKey !== this.country || !item.cityKey) return;
            if (!cities.has(item.cityKey)) cities.set(item.cityKey, item.city);
          });
        }

        const frag = document.createDocumentFragment();
        frag.appendChild(this.facetOption('city', '', this.allCitiesLabel));
        const entries = Array.from(cities.entries());
        if (!geo) entries.sort((a, b) => fold(a[1]).localeCompare(fold(b[1])));
        entries.forEach(([key, label]) => frag.appendChild(this.facetOption('city', key, label)));

        this.cityList.replaceChildren(frag);
      }

      facetOption(kind, key, label) {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        // Country labels are already cased (canonicalCountryName) ; city labels
        // are downcased at the source and re-cased by .pm-stockist__menu-city.
        button.className = `pm-stockist__menu-${kind}`;
        button.textContent = label;
        if (key === (kind === 'country' ? this.country : this.city)) {
          button.setAttribute('aria-current', 'true');
        }
        button.addEventListener('click', () => {
          if (kind === 'country') {
            if (this.countryEl) this.countryEl.open = false;
            // goToCountry drops the city on the way : a city only means
            // something inside its own country, and keeping it across a country
            // change would bring the list back empty. The blank key is the
            // "all countries" option, which is the index view itself.
            if (key) this.goToCountry(key);
            else this.goToIndex();
          } else {
            if (this.cityEl) this.cityEl.open = false;
            this.selectCity(key);
          }
        });
        li.appendChild(button);
        return li;
      }

      /* --- filtering -------------------------------------------------------- */

      applyQuery() {
        const next = this.input ? this.input.value.trim() : '';
        if (next === this.query) return;
        this.query = next;
        // Typing is not a navigation : replaceState, or the Back button would
        // have to walk back through every keystroke before leaving the page.
        this.syncUrl(true);
        this.refresh();
      }

      /* Search, country, city and the distance sort all go at once - a Clear
         that left the list still sorted by distance would read as broken. */
      clearAll() {
        this.query = '';
        this.country = '';
        this.continent = '';
        this.city = '';
        if (this.input) this.input.value = '';
        this.syncSearchClear();
        if (this.countryLabelEl) this.countryLabelEl.textContent = this.allCountriesLabel;
        if (this.cityLabelEl) this.cityLabelEl.textContent = this.allCitiesLabel;
        this.facets.forEach((facet) => {
          facet.open = false;
        });
        this.dropGeo();
        this.syncUrl();
        this.render();
        if (this.input) this.input.focus();
      }

      hasActiveFilters() {
        return Boolean(this.query || this.country || this.continent || this.city || this.origin);
      }

      /* Every token must match, so "paris 75008" and "lausanne abc" both work. */
      filter() {
        const tokens = fold(this.query).split(/\s+/).filter(Boolean);
        const perCountry = new Map();
        let visible = 0;

        this.items.forEach((item) => {
          let match = !this.country || item.groupKey === this.country;
          if (match && this.city) match = item.cityKey === this.city;
          if (match && tokens.length) match = tokens.every((token) => item.search.indexOf(token) !== -1);
          item.el.hidden = !match;
          if (match) {
            visible += 1;
            perCountry.set(item.groupKey, (perCountry.get(item.groupKey) || 0) + 1);
          }
        });

        this.groups.forEach((heading, key) => {
          heading.hidden = !perCountry.get(key);
        });

        if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
        if (this.clearBtn) this.clearBtn.hidden = !this.hasActiveFilters();
        this.updateCount(visible);
      }

      updateCount(visible) {
        if (!this.countEl) return;
        // The index and continent views have no list on screen, so counting
        // what is visible there would read 0 : each counts its own directory
        // subset instead (filter() never gates on continent, only on country/
        // city/query, so "visible" itself stays the unfiltered world total
        // while browsing a continent). And while the extra pages are missing
        // the only honest number is the server total.
        const mode = this.mode;
        let value = visible;
        if (mode === 'index') value = this.items.length;
        else if (mode === 'continent') value = this.continentCounts.get(this.continent) || 0;
        const unfiltered = !this.query && !this.country && !this.city && !this.continent;
        if (!this.hydrated && unfiltered) value = this.total || visible;
        this.countEl.textContent = this.countLabel.replace('[count]', value);
      }

      /* --- geolocation ------------------------------------------------------ */

      toggleGeo() {
        if (this.origin) {
          this.dropGeo();
          this.render();
          return;
        }

        if (!navigator.geolocation) {
          this.showMessage(this.dataset.geoError);
          return;
        }

        this.nearBtn.setAttribute('aria-busy', 'true');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.nearBtn.removeAttribute('aria-busy');
            this.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.nearBtn.setAttribute('aria-pressed', 'true');
            this.hideMessage();
            this.applyDistances();
            this.render();
          },
          () => {
            this.nearBtn.removeAttribute('aria-busy');
            this.showMessage(this.dataset.geoError);
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
        );
      }

      /* Forgets the held position and wipes the distance column. Leaves the
         repaint to the caller, so Clear can reset everything in one render. */
      dropGeo() {
        this.origin = null;
        // Outside a country the city came from the nearby list, which dies
        // with the position ; leaving it set would filter the whole directory
        // on a town the facet no longer even offers.
        if (!this.country) {
          this.city = '';
          if (this.cityLabelEl) this.cityLabelEl.textContent = this.allCitiesLabel;
        }
        this.items.forEach((item) => {
          item.distance = null;
          if (item.distanceEl) item.distanceEl.textContent = '';
        });
        if (this.nearBtn) this.nearBtn.setAttribute('aria-pressed', 'false');
        this.hideMessage();
      }

      applyDistances() {
        if (!this.origin) return;
        this.items.forEach((item) => {
          if (item.lat === null || item.lng === null) {
            item.distance = null;
            return;
          }
          item.distance = haversineKm(this.origin, { lat: item.lat, lng: item.lng });
          if (item.distanceEl) item.distanceEl.textContent = this.formatDistance(item.distance);
        });
      }

      formatDistance(km) {
        const value = this.unit === 'mi' ? km * MI_PER_KM : km;
        const rounded = value < 10 ? value.toFixed(1) : Math.round(value);
        return `${rounded} ${this.unit}`;
      }

      /* --- messages --------------------------------------------------------- */

      showMessage(text) {
        if (!this.messageEl || !text) return;
        this.messageEl.textContent = text;
        this.messageEl.hidden = false;
      }

      hideMessage() {
        if (!this.messageEl) return;
        this.messageEl.hidden = true;
      }
    }
  );
}
